// The decisions an admin makes on the WhatsApp-paste page. Everything the template
// actually says is read-only (see #93); this is the part a machine must not guess.
//
// Kept out of the page component so the "what is still undecided" rule is one testable
// function rather than a chain of `&&` inside JSX.

import type { PATIENT_CONDITIONS } from "@/lib/constants";

export type Severity = "error" | "warning" | "info";

export interface Issue {
	// Dotted path into the parsed data ("patient.condition"); empty for form-level findings.
	field?: string;
	// Stable contract on the backend side; the page uses it to keep UNKNOWN_LABEL out of
	// the findings list, since those lines already have a section of their own.
	code: string;
	severity: Severity;
	// Already Indonesian and ready to show — the backend owns the wording so this side
	// keeps no code→text table.
	messageId: string;
	// The snippet the finding came from, when the parser could point at one.
	rawValue?: string;
}

export interface PatientMatch {
	patientId: string;
	patientNumber: string;
	name: string;
	gender: string;
	dateOfBirth: string;
	age: number;
	contactPhone: string;
	contactEmail: string;
	activeAddress?: { address: string; locationCity: string } | null;
	score: number;
	confidence: string;
	// Already human-readable and Indonesian, straight from the matcher.
	reasons: string[] | null;
}

export interface AddressMatch {
	patientAddressId: number;
	addressId: number;
	address: string;
	notes: string;
	postalCode: string;
	locationId: number;
	locationCity: string;
	active: boolean;
	// null when either side has no coordinates — not 0, which would read as "same spot".
	distanceKm: number | null;
	score: number;
	confidence: string;
	reasons: string[] | null;
}

export interface PackageOption {
	id: number;
	name: string;
	numberOfVisit: number;
	totalPrice: number;
	active: boolean;
}

export interface LocationOption {
	id: number;
	city: string;
	state: string;
	score: number;
}

export type PatientChoice =
	| { mode: "existing"; patientId: string }
	| { mode: "new" };

export type AddressChoice =
	| { mode: "existing"; patientAddressId: number }
	| { mode: "new" };

export interface Decisions {
	// null means undecided, and stays null until the admin says otherwise. There is no
	// "first candidate wins" fallback anywhere in this file on purpose.
	patient: PatientChoice | null;
	address: AddressChoice | null;
	locationId: number | null;
	serviceId: number | null;
	packageId: number | null;
	// The backend's own code (see WA_CONDITIONS), not the booking form's value: the write
	// endpoint validates against its codes, and Rails translates on the way back out.
	condition: string | null;
}

// The patient's condition as the backend's WA validator spells it, beside the booking
// form's value for the same thing. Mirrors FORM_DATA_VOCABULARY in appointment_draft.rb,
// which turns the first into the second when the draft reaches the booking form.
export const WA_CONDITIONS: readonly {
	code: string;
	value: (typeof PATIENT_CONDITIONS)[number];
}[] = [
	{ code: "NORMAL", value: "NORMAL" },
	{ code: "HANYA DUDUK", value: "ONLY ABLE TO SIT" },
	{ code: "TIRAH BARING", value: "BEDRIDDEN" },
	{ code: "BELUM DIINFORMASIKAN", value: "NOT YET INFORMED" },
];

export type DecisionKey =
	| "patient"
	| "address"
	| "city"
	| "service"
	| "package"
	| "condition";

export const NO_DECISIONS: Decisions = {
	patient: null,
	address: null,
	locationId: null,
	serviceId: null,
	packageId: null,
	condition: null,
};

// Radio values are strings, so the two shapes share one encoding: "new" or "existing:<id>".
export const NEW_CHOICE = "new";

export function patientChoiceValue(choice: PatientChoice | null): string {
	if (!choice) return "";
	return choice.mode === "new" ? NEW_CHOICE : `existing:${choice.patientId}`;
}

export function addressChoiceValue(choice: AddressChoice | null): string {
	if (!choice) return "";
	return choice.mode === "new"
		? NEW_CHOICE
		: `existing:${choice.patientAddressId}`;
}

export function choosePatient(decisions: Decisions, value: string): Decisions {
	const patient: PatientChoice =
		value === NEW_CHOICE
			? { mode: "new" }
			: { mode: "existing", patientId: value.replace("existing:", "") };

	// Address candidates belong to the patient, so a switch invalidates any address
	// already picked — and with it the city that came along for the ride, plus everything
	// the city decides: which services reach it, and which packages that service sells.
	return {
		...decisions,
		patient,
		address: null,
		locationId: null,
		serviceId: null,
		packageId: null,
	};
}

export function chooseAddress(decisions: Decisions, value: string): Decisions {
	const address: AddressChoice =
		value === NEW_CHOICE
			? { mode: "new" }
			: {
					mode: "existing",
					patientAddressId: Number(value.replace("existing:", "")),
				};

	// An existing address already has a city; only a new one needs one chosen. Either way
	// the city may now be a different one, so the service and package it scoped are dropped.
	return {
		...decisions,
		address,
		locationId: null,
		serviceId: null,
		packageId: null,
	};
}

// Pre-fills the condition from what the backend recognised in the text, so the admin only
// confirms it. Optional on purpose: a backend from before fisiohome-backend#245 sends no
// suggestion, and then the admin picks — the step never fills from a guess.
export function withSuggestedCondition(
	decisions: Decisions,
	suggested: { condition?: string | null } | null | undefined,
): Decisions {
	if (decisions.condition !== null) return decisions;
	const code = suggested?.condition;
	if (!WA_CONDITIONS.some((option) => option.code === code)) return decisions;
	return { ...decisions, condition: code ?? null };
}

// Address candidates are only ever the selected patient's. Before a patient is chosen —
// or when the admin says "new patient" — there is nothing to offer, whatever the last
// preview happened to carry.
export function addressCandidatesFor(
	decisions: Decisions,
	candidates: AddressMatch[] | null | undefined,
): AddressMatch[] {
	if (decisions.patient?.mode !== "existing") return [];
	return candidates ?? [];
}

// What is still open, in the order the page asks for it. Drives both the per-section
// "belum diputuskan" marker and the summary, so the two can never disagree.
export function pendingDecisions(decisions: Decisions): DecisionKey[] {
	const pending: DecisionKey[] = [];

	if (!decisions.patient) pending.push("patient");
	// An address cannot be decided before the patient whose addresses these are.
	else if (!decisions.address) pending.push("address");

	if (decisions.address?.mode === "new" && decisions.locationId === null) {
		pending.push("city");
	}
	// The service is offered per city, so it cannot be asked for before one is settled —
	// and the package belongs to the service, so it waits on that in turn.
	if (decisions.serviceId === null) pending.push("service");
	else if (decisions.packageId === null) pending.push("package");

	// Rests on nothing above it, so it is asked last rather than wedged into the cascade.
	if (decisions.condition === null) pending.push("condition");

	return pending;
}

// Whether this one decision has been answered — which is not the same question as whether
// it is in `pendingDecisions`. That list is ordered and skips what cannot be asked yet: with
// no patient chosen, the address is absent from it because it is unreachable, not because it
// is settled. A caller that reads "absent from pending" as "done" ticks off steps nobody
// made, and then offers a way back into a step it will not reopen.
export function isDecided(decisions: Decisions, key: DecisionKey): boolean {
	switch (key) {
		case "patient":
			return !!decisions.patient;
		case "address":
			return !!decisions.address;
		case "city":
			return decisions.locationId !== null;
		case "service":
			return decisions.serviceId !== null;
		case "package":
			return decisions.packageId !== null;
		case "condition":
			return decisions.condition !== null;
	}
}

// The backend does not inspect findings when it writes, so this is the whole guard. It
// reads every finding, including the UNKNOWN_LABEL ones the findings card hides: what is
// not shown in that list still blocks if it is heavy.
export function hasBlockingIssues(issues: Issue[] | null | undefined): boolean {
	return (issues ?? []).some((issue) => issue.severity === "error");
}

// The one severe finding an admin decision answers: fisiohome-backend#245 reports a blank
// or unreadable template line 8 as severe, yet the condition step is where it is fixed.
// Shown as a warning once answered rather than dropped, so the admin still sees what the
// text was missing. AppointmentDraftsController#create_from_wa_form applies the same rule.
export function issuesAfterDecisions(
	issues: Issue[] | null | undefined,
	decisions: Decisions,
): Issue[] {
	return (issues ?? []).map((issue) =>
		issue.severity === "error" &&
		issue.field === "patient.condition" &&
		decisions.condition !== null
			? { ...issue, severity: "warning" }
			: issue,
	);
}

// Heaviest first. The backend returns findings in the order it met them, which puts the one
// finding that kills the save button below three notes often enough to matter.
const SEVERITY_ORDER: Severity[] = ["error", "warning", "info"];

// Findings gathered by severity, heaviest group first, empty groups dropped. Grouped rather
// than sorted flat so the page can say "these two are why Save is dead" once per group
// instead of stamping the same word on every row.
export function groupIssuesBySeverity(
	issues: Issue[] | null | undefined,
): { severity: Severity; issues: Issue[] }[] {
	const all = issues ?? [];

	return SEVERITY_ORDER.map((severity) => ({
		severity,
		// filter, so the backend's own order survives inside a group — the parser reads the
		// template top to bottom, and that is the order an admin walks the text in.
		issues: all.filter((issue) => issue.severity === severity),
	})).filter((group) => group.issues.length > 0);
}

// The decisions block as the write endpoint wants it. Flat and snake_cased here rather
// than reshaped server-side, so the one place that knows which choice means what is the
// same file that collected the choices.
//
export function decisionsPayload(decisions: Decisions) {
	return {
		patient_mode: decisions.patient?.mode ?? "new",
		patient_id:
			decisions.patient?.mode === "existing"
				? decisions.patient.patientId
				: null,
		address_mode: decisions.address?.mode ?? "new",
		patient_address_id:
			decisions.address?.mode === "existing"
				? decisions.address.patientAddressId
				: null,
		// Only a new address needs one; an existing address brings its own.
		location_id:
			decisions.address?.mode === "new" ? decisions.locationId : null,
		service_id: decisions.serviceId,
		package_id: decisions.packageId,
		condition: decisions.condition,
	};
}

export function activePackages(
	packages: PackageOption[] | null | undefined,
): PackageOption[] {
	return (packages ?? []).filter((option) => option.active);
}

// What the parser read, as it travels: sections at the top, an open record of fields
// under each. Open rather than typed field by field because a field added to the parser
// must reach the page without a matching change here.
export type ParsedData = Record<string, Record<string, unknown>>;

// Which parsed fields each decision actually rests on. Deliberately not the page's
// STEP_FIELDS, which answers a different question — which fields to *show* beside a step:
// it hands `numberOfVisit` to the service step as a reading aid, but a service is picked
// from the cities it reaches, never from a visit count. The count decides the package.
//
// A field named by nobody here — everything under "other details", so the complaint, the
// medical history, the landmark, the maps link — cannot invalidate any decision. That is
// the point: correcting one is the common edit, and it contradicts nothing that was
// already chosen.
const DECISION_FIELDS: Record<DecisionKey, readonly string[]> = {
	patient: [
		"fullName",
		"gender",
		"dateOfBirth",
		"age",
		// The PIC block: the matcher reads it, and an admin recognises a patient by it when
		// the name is spelled differently.
		"contactName",
		"contactPhone",
		"email",
	],
	address: ["street", "kelurahan", "kecamatan", "cityText", "postalCode"],
	// Only what the text says, not `locationCity`: that one is the backend's own resolution
	// of the city and can differ between two readings of the *same* text — a reading scoped
	// to a decided patient resolves an ambiguous city from that patient's existing address,
	// an unscoped one does not. The admin's pick was made from the candidates `cityText`
	// produced, so `cityText` is what can invalidate it.
	city: ["cityText"],
	// Nothing. A service is chosen from the list that reaches the decided city, so it is the
	// city cascade below — never the text — that can take it away.
	service: [],
	package: ["numberOfVisit"],
	condition: ["condition"],
};

// Field names are unique across the parser's sections, so a decision can name the fields it
// rests on without also naming which section they live in. Walked rather than kept as a
// field→section table: a second copy of the parser's shape is a second thing to keep true.
function fieldValue(data: ParsedData, field: string): unknown {
	for (const section of Object.values(data)) {
		if (section && field in section) return section[field];
	}
	return undefined;
}

// JSON rather than `!==` so the one array the parser returns compares by contents. Both
// sides come straight from JSON, so there is nothing here that cannot survive the trip.
function sameValue(a: unknown, b: unknown): boolean {
	return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * The decisions that a new reading has not contradicted.
 *
 * An admin who corrects a line in the pasted text should not have to answer the same five
 * questions again — most edits touch fields no decision rests on. So this compares the new
 * reading against the one the decisions were made against, field by field, and drops only
 * what the change reaches.
 *
 * Dependency order is what makes it safe: each decision rests on the one before it, so the
 * first one the new reading contradicts takes everything below it along. Anything above it
 * is untouched — editing the street re-opens the address, never the patient.
 */
export function reconcileDecisions(
	decisions: Decisions,
	previous: ParsedData | null | undefined,
	next: ParsedData | null | undefined,
	nextPackages: PackageOption[] | null | undefined,
): Decisions {
	// A first reading decides nothing — it only makes deciding possible. With nothing to
	// compare against, keeping what is there is the honest answer: this function exists to
	// drop what a *change* contradicts, and no change has been seen.
	if (!previous || !next) return decisions;

	const changed = (key: DecisionKey) =>
		DECISION_FIELDS[key].some(
			(field) =>
				!sameValue(fieldValue(previous, field), fieldValue(next, field)),
		);

	// Outside the cascade: it rests on its own line of the text and nothing else, so a
	// different patient leaves it standing and only an edit to that line re-asks it.
	return {
		...reconcileCascade(decisions, changed, nextPackages),
		condition: changed("condition") ? null : decisions.condition,
	};
}

function reconcileCascade(
	decisions: Decisions,
	changed: (key: DecisionKey) => boolean,
	nextPackages: PackageOption[] | null | undefined,
): Decisions {
	if (changed("patient")) return NO_DECISIONS;
	if (changed("address")) {
		return {
			...decisions,
			address: null,
			locationId: null,
			serviceId: null,
			packageId: null,
		};
	}
	if (changed("city")) {
		return { ...decisions, locationId: null, serviceId: null, packageId: null };
	}
	// A corrected visit count is a correction of exactly what a package encodes, so the pick
	// made against the old count is re-asked rather than kept. Picking a package that sells
	// a different number of visits stays allowed — `packagesByVisitMatch` never filtered the
	// list — but it has to be chosen again, deliberately, against the count now on screen.
	if (changed("package")) return { ...decisions, packageId: null };
	// The package list is re-read along with the service, so a package can be withdrawn
	// between two readings without any field above changing at all.
	if (
		decisions.packageId !== null &&
		!(nextPackages ?? []).some((option) => option.id === decisions.packageId)
	) {
		return { ...decisions, packageId: null };
	}

	return decisions;
}

// How many visits the template asked for, when the parser could read a count ("8x").
// Typed unknown on the way in because the parsed data travels as an open record, and
// anything that is not a plain positive whole number is "the text did not say" rather than
// a number to match packages against.
export function requestedVisitCount(
	data: ParsedData | null | undefined,
): number | null {
	const value = data?.scheduling?.numberOfVisit;
	return typeof value === "number" && Number.isInteger(value) && value > 0
		? value
		: null;
}

// The packages that answer the requested visit count first, everything else after in the
// order the backend sent it. Sorted rather than filtered: a text asking for 8x is still
// only a request, and the admin may well know the patient meant 12x.
//
// Relies on Array#sort being stable (it is, per spec since ES2019), so the non-matching
// tail keeps the backend's own ordering instead of being shuffled.
export function packagesByVisitMatch(
	packages: PackageOption[],
	requestedVisits: number | null,
): PackageOption[] {
	if (requestedVisits === null) return packages;

	const matches = (option: PackageOption) =>
		option.numberOfVisit === requestedVisits ? 0 : 1;
	return [...packages].sort((a, b) => matches(a) - matches(b));
}

export interface ServiceOption {
	id: number;
	name: string;
	// The cities this service is actually sold in, from the active rows of
	// location_services. Sent with the service rather than fetched per city: there are a
	// handful of services, so the whole map costs less than a second round trip.
	locationIds: number[];
}

// Which city the visit lands in, wherever that was settled. An existing address carries
// its own city; a new one only has the one the admin picked from the candidates.
//
// Null until one of those is true — and a null here is what keeps the service picker from
// offering a list scoped to nothing.
export function decidedLocationId(
	decisions: Decisions,
	candidates: AddressMatch[] | null | undefined,
): number | null {
	const address = decisions.address;
	if (!address) return null;
	if (address.mode === "new") return decisions.locationId;

	const chosen = (candidates ?? []).find(
		(candidate) => candidate.patientAddressId === address.patientAddressId,
	);
	// The candidate list is re-read on every edit, so an address chosen against an older
	// reading can genuinely be absent here. Undecided is the honest answer, not a guess.
	return chosen?.locationId ?? null;
}

// Only the services that reach that city. Offering the rest would let an admin pick a
// service nobody can actually deliver there.
export function servicesForLocation(
	services: ServiceOption[] | null | undefined,
	locationId: number | null,
): ServiceOption[] {
	if (locationId === null) return [];
	return (services ?? []).filter((service) =>
		service.locationIds.includes(locationId),
	);
}

// One line of the pasted text, plus whether the parser could read it.
export interface SourceLine {
	text: string;
	matched: boolean;
}

// The parser returns the lines it could not read, in the order it met them. Walked with a
// pointer rather than compared by content: a pasted template repeats identical lines often
// enough — blank lines, "-", a bare "ya" — that matching by text alone would light up the
// first occurrence instead of the one the parser actually stumbled on.
//
// If a returned line never matches (the parser normalised it before handing it back), the
// pointer stalls and the rest of the text simply renders unmarked. That degrades to "no
// highlighting", never to highlighting the wrong line.
export function markSourceLines(
	rawText: string,
	unmatchedLines: string[] | null | undefined,
): SourceLine[] {
	const pending = [...(unmatchedLines ?? [])];

	return rawText.split("\n").map((text) => {
		const isUnmatched = pending.length > 0 && pending[0].trim() === text.trim();
		if (isUnmatched) pending.shift();
		return { text, matched: !isUnmatched };
	});
}
